import axios from 'axios';

const API_URL = process.env.REACT_APP_API_URL || 'http://api.telecalx.com:8000';
const AUTH_URL = process.env.REACT_APP_AUTH_URL || 'http://auth.telecalx.com:8001';

const options = { withCredentials: true, headers: { 'Content-Type': 'application/json' } };
const client = axios.create({ baseURL: API_URL, ...options });
// Auth service: login, sign-up, sessions, organization tokens, invitation accept.
const authClient = axios.create({ baseURL: AUTH_URL, ...options });

// ---- login session -----------------------------------------------------------
// The access token (10 min) and refresh token live in HttpOnly cookies; JavaScript never sees them and
// nothing is stored in localStorage. When the access token expires we call /refresh once and retry.

const NO_SESSION_REFRESH = new Set([
  '/api/auth/login', '/api/auth/login/verify', '/api/auth/login/resend', '/api/auth/register',
  '/api/auth/refresh', '/api/auth/logout', '/api/auth/forgot-password', '/api/auth/reset-password',
  '/api/auth/change-password',
]);
const isTenantCall = (url = '') => url === '/api/tenant' || url.startsWith('/api/tenant/');
const channel = typeof BroadcastChannel !== 'undefined' ? new BroadcastChannel('tcx-auth') : null;
let refreshing = null;
let lastRefresh = { at: 0, session: null };
let refreshTimer = null;
let sessionEndedHandler = null;

export const setSessionEndedHandler = (fn) => { sessionEndedHandler = fn; };
export const onAuthBroadcast = (fn) => {
  if (!channel) return () => {};
  const listener = (e) => fn(e.data);
  channel.addEventListener('message', listener);
  return () => channel.removeEventListener('message', listener);
};
export const broadcastAuth = (message) => channel?.postMessage(message);

/** Re-arm the silent refresh ~1 minute before the access token expires. */
export function scheduleRefresh(expiresInSeconds) {
  clearTimeout(refreshTimer);
  if (!expiresInSeconds) return;
  const delay = Math.max(5, expiresInSeconds - 60) * 1000;
  refreshTimer = setTimeout(function tick() {
    if (document.hidden) {
      document.addEventListener('visibilitychange', tick, { once: true });
      return;
    }
    refreshSession().catch(() => {});
  }, delay);
}

export const stopRefreshTimer = () => clearTimeout(refreshTimer);

export function refreshSession() {
  if (!refreshing) {
    const run = async () => {
      // Another tab may have refreshed while we waited for the lock: reuse its result.
      if (Date.now() - lastRefresh.at < 5000 && lastRefresh.session) return lastRefresh.session;
      const res = await authClient.post('/api/auth/refresh');
      lastRefresh = { at: Date.now(), session: res.data };
      broadcastAuth({ type: 'refreshed', session: res.data });
      return res.data;
    };
    const locked = typeof navigator !== 'undefined' && navigator.locks
      ? navigator.locks.request('tcx-session-refresh', run)
      : run();
    refreshing = locked
      .then((session) => { scheduleRefresh(session.access_expires_in); return session; })
      .catch((err) => { stopRefreshTimer(); sessionEndedHandler?.(); throw err; })
      .finally(() => { refreshing = null; });
  }
  return refreshing;
}

onAuthBroadcast((msg) => {
  if (msg?.type === 'refreshed') {
    lastRefresh = { at: Date.now(), session: msg.session };
    scheduleRefresh(msg.session?.access_expires_in);
  }
});

/** The non-HttpOnly marker cookie set by the auth service on every *.ROOT_DOMAIN host (no credential inside). Its name
 * starts with the server's COOKIE_PREFIX (staging has its own). */
const SIGNED_IN_COOKIE = `${process.env.REACT_APP_COOKIE_PREFIX || 'tcx'}_signed_in`;
export const signedInMarker = () => {
  const match = document.cookie.match(new RegExp(`(?:^|;\\s*)${SIGNED_IN_COOKIE}=([^;]*)`));
  return match ? match[1] : null;
};

/**
 * BroadcastChannel only reaches tabs on the same host, so tabs on other organizations' subdomains watch the
 * shared marker cookie instead: gone => logged out elsewhere; changed => someone else logged in.
 */
export function watchSignedInMarker(onChange) {
  let last = signedInMarker();
  const check = () => {
    if (document.hidden) return;
    const now = signedInMarker();
    if (now !== last) {
      const previous = last;
      last = now;
      onChange(now, previous);
    }
  };
  const timer = setInterval(check, 2000);
  document.addEventListener('visibilitychange', check);
  window.addEventListener('focus', check);
  return () => {
    clearInterval(timer);
    document.removeEventListener('visibilitychange', check);
    window.removeEventListener('focus', check);
  };
}

// ---- maintenance ----------------------------------------------------------------
// While TeleCalX is in maintenance, customer calls answer 503 with the details; MaintenanceGate listens and shows
// the maintenance page.
const maintenanceListeners = new Set();
export const onMaintenance = (fn) => { maintenanceListeners.add(fn); return () => maintenanceListeners.delete(fn); };
const noticeMaintenance = (response) => {
  if (response?.status === 503 && response.data?.maintenance) maintenanceListeners.forEach((fn) => fn(response.data.maintenance));
};

const retryAfterSessionRefresh = (instance) => async (error) => {
  const { config, response } = error;
  const url = config?.url || '';
  noticeMaintenance(response);
  if (response?.status === 401 && config && !config._sessionRetried && !NO_SESSION_REFRESH.has(url) && !isTenantCall(url)) {
    config._sessionRetried = true;
    await refreshSession();
    return instance(config);
  }
  return Promise.reject(error);
};
authClient.interceptors.response.use((res) => res, retryAfterSessionRefresh(authClient));

// ---- organization token --------------------------------------------------------
// Tenant tokens live only in memory; the session re-issues them.
let tenantToken = null;
let refreshTenantToken = null;

export const setTenantToken = (token) => { tenantToken = token; };
export const setTenantTokenRefresher = (fn) => { refreshTenantToken = fn; };

const ACCESS_LOST = new Set(['No access to this organization', 'Organization is not active']);

client.interceptors.request.use((config) => {
  if (tenantToken && isTenantCall(config.url)) {
    config.headers.Authorization = `Bearer ${tenantToken}`;
  }
  return config;
});

client.interceptors.response.use(
  (res) => res,
  async (error) => {
    const { config, response } = error;
    // 401: token expired. 403 on access itself: membership/org changed, so re-check
    // (the refresher switches the UI to the no-access screen if it is really gone).
    const accessLost = response?.status === 403 && ACCESS_LOST.has(response.data?.detail);
    if ((response?.status === 401 || accessLost) && isTenantCall(config?.url) && !config._retried && refreshTenantToken) {
      config._retried = true;
      await refreshTenantToken();
      return client(config);
    }
    return retryAfterSessionRefresh(client)(error);
  },
);

export const authAPI = {
  register: (data) => authClient.post('/api/auth/register', data),
  login: (email, password, rememberMe = false) =>
    authClient.post('/api/auth/login', { email, password, remember_me: rememberMe }),
  /** Two-factor sign-in: the code emailed after the password. */
  verifyLoginCode: (challengeId, code) => authClient.post('/api/auth/login/verify', { challenge_id: challengeId, code }),
  resendLoginCode: (challengeId) => authClient.post('/api/auth/login/resend', { challenge_id: challengeId }),
  logout: () => authClient.post('/api/auth/logout'),
  me: () => authClient.get('/api/auth/me'),
  updateMe: (data) => authClient.patch('/api/auth/me', data),
  refresh: () => refreshSession(),
  logoutAll: () => authClient.post('/api/auth/logout-all'),
  forgotPassword: (email) => authClient.post('/api/auth/forgot-password', { email }),
  checkResetToken: (token) => authClient.get(`/api/auth/reset-password/${encodeURIComponent(token)}`),
  resetPassword: (token, newPassword) => authClient.post('/api/auth/reset-password', { token, new_password: newPassword }),
  changePassword: (currentPassword, newPassword) =>
    authClient.post('/api/auth/change-password', { current_password: currentPassword, new_password: newPassword }),
  sessions: () => authClient.get('/api/auth/sessions'),
  endSession: (id) => authClient.delete(`/api/auth/sessions/${id}`),
  signOutOthers: () => authClient.post('/api/auth/sessions/sign-out-others'),
  tenantToken: (slug) => authClient.post('/api/auth/tenant-token', { slug }),
  createTenant: (name) => client.post('/api/tenants', { name }),
  getFeatureFlag: (name) => client.get(`/api/features/${name}`),
  website: () => client.get('/api/public/website'),
};

/** The signed-in person's own account (Settings): preferences, photo, two-factor sign-in, deleting the account. */
export const accountAPI = {
  get: () => authClient.get('/api/auth/account'),
  timezones: () => authClient.get('/api/auth/account/timezones'),
  savePreferences: (section, values) => authClient.put(`/api/auth/account/preferences/${section}`, values),
  saveAvatar: (image) => authClient.put('/api/auth/account/avatar', { image }),
  removeAvatar: () => authClient.delete('/api/auth/account/avatar'),
  emailTwoFactor: (enabled, password) => authClient.post('/api/auth/account/2fa/email', { enabled, password }),
  authenticatorSetup: (password) => authClient.post('/api/auth/account/2fa/authenticator/setup', { password }),
  authenticatorConfirm: (code) => authClient.post('/api/auth/account/2fa/authenticator/confirm', { code }),
  authenticatorDisable: (password) => authClient.post('/api/auth/account/2fa/authenticator/disable', { password }),
  backupCodes: (password) => authClient.post('/api/auth/account/2fa/backup-codes', { password }),
  deleteAccount: (password, confirm) => authClient.post('/api/auth/account/delete', { password, confirm }),
  /** ZIP of my details, preferences and this company's records I can see. */
  exportData: () => client.get('/api/tenant/account/export', { responseType: 'blob' }),
};

// The client defaults to JSON; axios fills in the multipart boundary.
const MULTIPART = { headers: { 'Content-Type': 'multipart/form-data' } };

/** Paying an invoice online with Razorpay Checkout. */
export const paymentsAPI = {
  available: () => client.get('/api/tenant/payments/razorpay'),
  order: (invoiceId) => client.post('/api/tenant/payments/razorpay/orders', { invoice_id: invoiceId }),
  verify: (result) => client.post('/api/tenant/payments/razorpay/verify', result),
};

/** Help & Support: support tickets with the TeleCalX team. Create / reply take FormData (files allowed). */
export const supportAPI = {
  categories: () => client.get('/api/tenant/support/categories'),
  list: (params) => client.get('/api/tenant/support/tickets', { params }),
  create: (form) => client.post('/api/tenant/support/tickets', form, MULTIPART),
  get: (id) => client.get(`/api/tenant/support/tickets/${id}`),
  reply: (id, form) => client.post(`/api/tenant/support/tickets/${id}/messages`, form, MULTIPART),
  close: (id) => client.post(`/api/tenant/support/tickets/${id}/close`),
  download: (id) => client.get(`/api/tenant/support/attachments/${id}`, { responseType: 'blob' }),
};

export const inviteAPI = {
  preview: (token) => authClient.get(`/api/invitations/${encodeURIComponent(token)}`),
  accept: (token, data = {}) => authClient.post(`/api/invitations/${encodeURIComponent(token)}/accept`, data),
};

export const publicAPI = {
  /** {active, title, message, ends_at, upcoming}: maintenance now, and the next scheduled window. */
  maintenance: () => client.get('/api/public/maintenance'),
};

export const tenantAPI = {
  get: () => client.get('/api/tenant'),
  /** The bell: {enabled, unread, notifications: [{id, title, body, level, link, created_at, read}]}. */
  notifications: () => client.get('/api/tenant/notifications'),
  /** Marks these notifications (or, with null, all) read for me. */
  markNotificationsRead: (ids) => client.post('/api/tenant/notifications/read', { ids }),
  /** Usage against the company's limits (Usage & Limits Control): [{resource, name, used, limit, percent, state, ...}] */
  usage: () => client.get('/api/tenant/usage'),
  rename: (name) => client.patch('/api/tenant', { name }),
  /** Company information (Settings › Profile): name, website, industry, timezone, date / time format. */
  update: (data) => client.patch('/api/tenant', data),
  stats: () => client.get('/api/tenant/stats'),
  /** Choose a fixed-price plan: its code, the billing cycle (monthly | yearly) and an optional coupon. */
  setSubscription: (plan, { billingCycle, coupon } = {}) => client.post('/api/tenant/subscription', {
    subscription_tier: plan, ...(billingCycle && { billing_cycle: billingCycle }), ...(coupon && { coupon }),
  }),
  checkCoupon: (code, plan) => client.post('/api/tenant/coupons/check', { code, plan }),
  invoices: () => client.get('/api/tenant/invoices'),
  invoice: (id) => client.get(`/api/tenant/invoices/${id}`),
  members: () => client.get('/api/tenant/members'),
  updateMember: (userId, data) => client.patch(`/api/tenant/members/${userId}`, data),
  removeMember: (userId) => client.delete(`/api/tenant/members/${userId}`),
  leave: () => client.post('/api/tenant/leave'),
  invitations: () => client.get('/api/tenant/invitations'),
  invite: (email, role) => client.post('/api/tenant/invitations', { email, role }),
  revokeInvitation: (id) => client.delete(`/api/tenant/invitations/${id}`),
  features: () => client.get('/api/tenant/features'),
  setFeature: (name, enabled) => client.patch(`/api/tenant/features/${name}`, { enabled }),
  auditLogs: () => client.get('/api/tenant/audit-logs'),
  /** The welcome tour was finished or skipped (action: 'finish' | 'skip'): it isn't shown again. */
  saveTour: (action) => client.post('/api/tenant/tour', { action }),
  profile: () => client.get('/api/tenant/profile'),
  saveProfile: (data) => client.put('/api/tenant/profile', data),
};

export const contactsAPI = {
  list: (params) => client.get('/api/tenant/contacts', { params }),
  stats: () => client.get('/api/tenant/contacts/stats'),
  create: (data) => client.post('/api/tenant/contacts', data),
  update: (id, data) => client.patch(`/api/tenant/contacts/${id}`, data),
  remove: (id) => client.delete(`/api/tenant/contacts/${id}`),
  exportCsv: (params) => client.get('/api/tenant/contacts/export', { params, responseType: 'blob' }),
};

export const appointmentsAPI = {
  list: (params) => client.get('/api/tenant/appointments', { params }),
  stats: () => client.get('/api/tenant/appointments/stats'),
  create: (data) => client.post('/api/tenant/appointments', data),
  update: (id, data) => client.patch(`/api/tenant/appointments/${id}`, data),
  remove: (id) => client.delete(`/api/tenant/appointments/${id}`),
  exportCsv: (params) => client.get('/api/tenant/appointments/export', { params, responseType: 'blob' }),
};

// Call Recordings: audio is fetched through the API with the normal sign-in plus a single-use access grant (sent as a
// header, never in a URL); its storage address is never sent to the browser. Downloads and deletions need a fresh
// step-up verification (stepUp / verifyStepUp).
const R = '/api/tenant/recordings';
export const recordingsAPI = {
  list: (params) => client.get(R, { params }),
  get: (id) => client.get(`${R}/${id}`),
  update: (id, data) => client.patch(`${R}/${id}`, data),
  remove: (id) => client.delete(`${R}/${id}`),
  /** purpose: play | download -> the audio as a blob. */
  audio: async (id, purpose = 'play') => {
    const { data } = await client.post(`${R}/${id}/access`, { purpose });
    return client.get(`${R}/${id}/audio`, { params: purpose === 'download' ? { download: true } : {}, responseType: 'blob',
      headers: { [data.header]: data.grant } });
  },
  stepUp: () => client.post(`${R}/step-up`),
  verifyStepUp: (challengeId, code) => client.post(`${R}/step-up/verify`, { challenge_id: challengeId, code }),
  settings: () => client.get(`${R}/settings`),
  saveSettings: (data) => client.put(`${R}/settings`, data),
};

export const callsAPI = {
  list: (params) => client.get('/api/tenant/calls', { params }),
  get: (id) => client.get(`/api/tenant/calls/${id}`),
  stats: () => client.get('/api/tenant/calls/stats'),
  daily: (days = 30) => client.get('/api/tenant/calls/daily', { params: { days } }),
  make: (data) => client.post('/api/tenant/calls', data),
  exportCsv: (params) => client.get('/api/tenant/calls/export', { params, responseType: 'blob' }),
  telephony: () => client.get('/api/tenant/telephony'),
  saveRouting: (data) => client.put('/api/tenant/telephony/routing', data),
};

export const flowsAPI = {
  list: () => client.get('/api/tenant/flows'),
  get: (id) => client.get(`/api/tenant/flows/${id}`),
  create: (name, draft) => client.post('/api/tenant/flows', { name, draft }),
  save: (id, data) => client.put(`/api/tenant/flows/${id}`, data),
  validate: (draft) => client.post('/api/tenant/flows/validate', draft),
  publish: (id) => client.post(`/api/tenant/flows/${id}/publish`),
  unpublish: (id) => client.post(`/api/tenant/flows/${id}/unpublish`),
  remove: (id) => client.delete(`/api/tenant/flows/${id}`),
};

export const workspaceAPI = {
  aiSettings: () => client.get('/api/tenant/ai-receptionist'),
  saveAiSettings: (data) => client.put('/api/tenant/ai-receptionist', data),
  leadSources: () => client.get('/api/tenant/analytics/lead-sources'),
  summary: () => client.get('/api/tenant/analytics/summary'),
  report: () => client.get('/api/tenant/analytics/report', { responseType: 'blob' }),
};

export default client;
